import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { LogOut, Phone, MessageCircle, Mail, FileText, Check, Clock, CreditCard, ReceiptText, Save, IdCard, Smartphone, AtSign, UserCircle2, BadgeCheck, Wallet, ExternalLink, FileDown } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, API, waLink, getErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { statusPill } from "./admin/AdminDashboard";

export default function Portal() {
  const { user, loading, logout, isLeadOrCustomer } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [profile, setProfile] = useState({});
  const [saving, setSaving] = useState(false);
  const [adminWa, setAdminWa] = useState("7008144614");

  const load = () => {
    api.get("/portal/me")
      .then((r) => {
        setData(r.data);
        setProfile({
          full_name: r.data.full_name || "",
          pan: r.data.pan || "",
          aadhaar: r.data.aadhaar || "",
          whatsapp: r.data.whatsapp || "",
          email: r.data.email || "",
          address: r.data.address || "",
          city: r.data.city || "",
          state: r.data.state || "",
          district: r.data.district || "",
          pincode: r.data.pincode || "",
        });
      })
      .catch((e) => toast.error(getErr(e, "Could not load your profile")))
      .finally(() => setBusy(false));
  };

  useEffect(() => { if (isLeadOrCustomer) load(); /* eslint-disable-next-line */ }, [isLeadOrCustomer]);
  useEffect(() => {
    api.get("/config/public").then((r) => r.data?.admin_whatsapp && setAdminWa(r.data.admin_whatsapp)).catch(() => {});
  }, []);

  if (loading) return <div className="min-h-screen grid place-items-center text-[#5C6661]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isLeadOrCustomer) return <Navigate to="/admin" replace />;
  if (busy || !data) return <div className="min-h-screen grid place-items-center text-[#5C6661]">Loading your space…</div>;

  const isCustomer = data.role === "customer";
  const fullName = data.full_name || "there";
  const ref = data.ref;
  const services = isCustomer ? (data.service_history || []) : [{
    service_name: data.service_name, price: data.service_price, status: data.status, started_at: data.created_at,
  }];

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.patch("/portal/me", profile);
      toast.success("Profile saved");
      setEditMode(false);
      load();
    } catch (e) { toast.error(getErr(e, "Could not save profile")); }
    finally { setSaving(false); }
  };

  const onLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#F3F1EC]" data-testid="portal-page">
      <Toaster richColors position="top-right" />

      {/* Top bar */}
      <header className="glass-nav sticky top-0 z-30 border-b border-[#E5E2DC]">
        <div className="container-pad mx-auto max-w-[1100px] flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-[#1E4D3B] grid place-items-center text-[#FDFCFB] font-display font-semibold text-sm">F</div>
            <div className="leading-tight">
              <div className="font-display font-semibold text-[#121C17]">FinSahayak</div>
              <div className="text-[10px] text-[#5C6661] uppercase tracking-wider">{isCustomer ? "Customer" : "Lead"} Portal</div>
            </div>
          </Link>
          <button onClick={onLogout} data-testid="portal-logout"
            className="rounded-full px-4 py-2 text-sm font-medium border border-[#E5E2DC] bg-white inline-flex items-center gap-1.5 hover:bg-[#F3F1EC]">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="container-pad mx-auto max-w-[1100px] py-8 md:py-12">
        {/* Greeting + role-status badge */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <span className="label-eyebrow">{isCustomer ? "Active customer" : "Lead in progress"}</span>
            <h1 className="mt-2 text-3xl sm:text-4xl font-display font-medium tracking-tight text-[#121C17]" data-testid="portal-greeting">
              Hi {fullName.split(" ")[0]} 👋
            </h1>
            <p className="mt-2 text-sm text-[#5C6661]">Reference <span className="font-mono text-[#121C17] font-medium">{ref}</span></p>
          </div>
          {isCustomer ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-[#1E4D3B] text-white" data-testid="portal-role-badge">
              <BadgeCheck size={14} /> Converted Customer
            </span>
          ) : (
            <span className={`text-xs font-semibold px-3 py-2 rounded-full ${statusPill(data.status)}`} data-testid="portal-role-badge">{data.status}</span>
          )}
        </div>

        {/* Money + Pay Now */}
        {isCustomer && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="portal-money-summary">
            <div className="bento-card p-5"><div className="text-xs label-eyebrow">Total paid</div><div className="mt-2 text-2xl font-display font-medium text-emerald-700">₹{(data.total_paid || 0).toLocaleString("en-IN")}</div></div>
            <div className="bento-card p-5"><div className="text-xs label-eyebrow">Outstanding</div><div className="mt-2 text-2xl font-display font-medium text-amber-700">₹{(data.total_due || 0).toLocaleString("en-IN")}</div></div>
            <div className="bento-card p-5"><div className="text-xs label-eyebrow">Services</div><div className="mt-2 text-2xl font-display font-medium text-[#1E4D3B]">{services.length}</div></div>
            <div className="bento-card p-5"><div className="text-xs label-eyebrow">Customer since</div><div className="mt-2 text-sm text-[#5C6661]">{new Date(data.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div></div>
          </div>
        )}

        {(isCustomer && (data.total_due || 0) > 0) && (
          <PayBento data={data} ref={ref} onPaid={load} adminWa={adminWa} />
        )}

        {/* Lead pay-now bento: lets a fresh lead pay for their service straight from the portal */}
        {(!isCustomer && data.service_price > 0) && (
          <LeadPayBento data={data} ref={ref} onPaid={load} adminWa={adminWa} />
        )}

        {/* PROFILE */}
        <section className="mt-8 bento-card p-5 sm:p-6" data-testid="portal-profile">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1E4D3B]/10 text-[#1E4D3B] grid place-items-center"><UserCircle2 size={20} /></div>
              <div>
                <span className="label-eyebrow text-[10px]">Your profile</span>
                <h3 className="font-display font-medium text-lg text-[#121C17] -mt-0.5">Personal details</h3>
              </div>
            </div>
            {!editMode ? (
              <button onClick={() => setEditMode(true)} data-testid="portal-profile-edit"
                className="rounded-full px-4 py-2 text-xs font-medium border border-[#E5E2DC] hover:bg-[#F3F1EC]">Edit profile</button>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => { setEditMode(false); load(); }} className="rounded-full px-4 py-2 text-xs font-medium border border-[#E5E2DC]">Cancel</button>
                <button onClick={saveProfile} disabled={saving} data-testid="portal-profile-save"
                  className="btn-primary rounded-full px-4 py-2 text-xs font-medium inline-flex items-center gap-1 disabled:opacity-60">
                  <Save size={12} /> {saving ? "Saving…" : "Save"}
                </button>
              </div>
            )}
          </div>

          <dl className="grid sm:grid-cols-2 gap-4 text-sm">
            <ReadOrEdit label="Full name" icon={UserCircle2} value={data.full_name} edit={editMode} v={profile.full_name} on={(v) => setProfile({ ...profile, full_name: v })} test="profile-full_name" />
            <ReadOrEdit label="Mobile (login id)" icon={Smartphone} value={`+91 ${data.mobile}`} edit={false} mono />
            <ReadOrEdit label="WhatsApp" icon={MessageCircle} value={data.whatsapp ? `+91 ${data.whatsapp}` : "—"} edit={editMode} v={profile.whatsapp} on={(v) => setProfile({ ...profile, whatsapp: v })} test="profile-whatsapp" />
            <ReadOrEdit label="Email" icon={AtSign} value={data.email} edit={editMode} v={profile.email} on={(v) => setProfile({ ...profile, email: v })} test="profile-email" />
            <ReadOrEdit label="PAN Number" icon={IdCard} value={data.pan || "—"} edit={editMode} v={profile.pan} on={(v) => setProfile({ ...profile, pan: v.toUpperCase() })} test="profile-pan" mono placeholder="ABCDE1234F" />
            <ReadOrEdit label="Aadhaar Number" icon={IdCard} value={data.aadhaar || "—"} edit={editMode} v={profile.aadhaar} on={(v) => setProfile({ ...profile, aadhaar: v })} test="profile-aadhaar" mono placeholder="12-digit number" />
            <div className="sm:col-span-2">
              <ReadOrEdit label="Address" icon={FileText} value={`${data.address || ""}, ${data.city || ""}, ${data.district || ""}, ${data.state || ""} — ${data.pincode || ""}`} edit={editMode} v={profile.address} on={(v) => setProfile({ ...profile, address: v })} test="profile-address" />
            </div>
          </dl>
        </section>

        {/* SERVICES */}
        <section className="mt-8" data-testid="portal-services">
          <span className="label-eyebrow">Your services & updates</span>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            {services.map((s, i) => (
              <div key={i} className="bento-card p-5" data-testid={`portal-service-${i}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#1E4D3B]/10 text-[#1E4D3B] grid place-items-center"><ReceiptText size={18} /></div>
                    <div>
                      <h3 className="font-display font-medium text-base text-[#121C17]">{s.service_name}</h3>
                      <div className="text-xs text-[#5C6661]">Since {new Date(s.started_at || data.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full ${s.status === "Active" ? "pill-completed" : statusPill(s.status)}`}>{s.status || "Active"}</span>
                </div>
                <div className="text-sm text-[#5C6661]">Price <span className="font-medium text-[#121C17]">₹{(s.price || 0).toLocaleString("en-IN")}</span></div>
                {isCustomer && s.renewal_date && (
                  <div className="mt-1 text-xs text-[#5C6661]">Renewal: <span className="font-medium text-[#121C17]">{s.renewal_date}</span></div>
                )}
                {/* Monthly status preview for customers */}
                {isCustomer && s.monthly_status && Object.keys(s.monthly_status).length > 0 && (
                  <MonthlyPreview monthly={s.monthly_status} />
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Documents */}
        {data.documents?.length > 0 && (
          <section className="mt-8" data-testid="portal-documents">
            <span className="label-eyebrow">Documents you submitted</span>
            <ul className="mt-4 bento-card divide-y divide-[#E5E2DC]">
              {data.documents.map((d) => (
                <li key={d.id} className="p-4 flex items-center gap-3">
                  <FileText size={18} className="text-[#1E4D3B]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#121C17] truncate">{d.filename}</div>
                    <div className="text-xs text-[#5C6661]">{(d.size / 1024).toFixed(1)} KB · {d.content_type}</div>
                  </div>
                  <Check size={14} className="text-emerald-600" />
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Timeline for leads */}
        {!isCustomer && data.timeline?.length > 0 && (
          <section className="mt-8" data-testid="portal-timeline">
            <span className="label-eyebrow">Service updates</span>
            <ol className="mt-4 bento-card p-6 space-y-3">
              {data.timeline.slice().reverse().map((t) => (
                <li key={t.id} className="pl-4 border-l-2 border-[#1E4D3B] text-sm">
                  <div className="text-[#121C17]">{t.event}</div>
                  <div className="text-xs text-[#5C6661]">{new Date(t.at).toLocaleString("en-IN")}</div>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Payments for customers */}
        {isCustomer && data.payments?.length > 0 && (
          <section className="mt-8" data-testid="portal-payments">
            <span className="label-eyebrow">Payment history</span>
            <ul className="mt-4 bento-card divide-y divide-[#E5E2DC]">
              {data.payments.slice().reverse().map((p) => (
                <li key={p.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`portal-payment-${p.id}`}>
                  <CreditCard size={18} className="text-emerald-600" />
                  <div className="flex-1 min-w-[140px]">
                    <div className="text-sm font-medium text-[#121C17]">₹{p.amount.toLocaleString("en-IN")} via {p.method}</div>
                    <div className="text-xs text-[#5C6661]">{new Date(p.created_at).toLocaleString("en-IN")}</div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full pill-completed">Received</span>
                  <a href={`${API}/receipts/${p.id}`} target="_blank" rel="noopener noreferrer"
                    data-testid={`portal-receipt-${p.id}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#1E4D3B] hover:underline">
                    <FileDown size={12} /> Download receipt
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Help */}
        <section className="mt-8 bento-card p-6" data-testid="portal-help">
          <h3 className="font-display font-medium text-lg text-[#121C17]">Need help?</h3>
          <p className="mt-1 text-sm text-[#5C6661]">Our team replies on WhatsApp within 10 minutes during business hours.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a href={waLink(`Hi, my reference is ${ref}.`)} target="_blank" rel="noopener noreferrer"
              data-testid="portal-whatsapp"
              className="rounded-full px-4 py-2 text-sm font-medium text-white inline-flex items-center gap-1.5" style={{ background: "#25D366" }}>
              <MessageCircle size={14} /> WhatsApp us
            </a>
            <a href="tel:+919999999999" data-testid="portal-call"
              className="rounded-full px-4 py-2 text-sm font-medium border border-[#E5E2DC] bg-white inline-flex items-center gap-1.5 hover:bg-[#F3F1EC]"><Phone size={14} /> Call</a>
            <a href="mailto:hello@finsahayak.com" data-testid="portal-email"
              className="rounded-full px-4 py-2 text-sm font-medium border border-[#E5E2DC] bg-white inline-flex items-center gap-1.5 hover:bg-[#F3F1EC]"><Mail size={14} /> Email</a>
          </div>
        </section>
      </main>
    </div>
  );
}

function ReadOrEdit({ label, icon: Icon, value, edit, v, on, test, mono, placeholder }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-[#5C6661] flex items-center gap-1.5">{Icon && <Icon size={11} />} {label}</dt>
      {edit ? (
        <input value={v} onChange={(e) => on?.(e.target.value)} placeholder={placeholder} data-testid={test}
          className={`mt-2 w-full bg-white border border-[#E5E2DC] rounded-xl px-3 py-2 text-sm focus:border-[#1E4D3B] ${mono ? "font-mono" : ""}`} />
      ) : (
        <dd className={`mt-1.5 text-[#121C17] ${mono ? "font-mono" : ""}`}>{value || "—"}</dd>
      )}
    </div>
  );
}

function MonthlyPreview({ monthly }) {
  const years = Object.keys(monthly).sort().reverse();
  const year = years[0];
  if (!year) return null;
  const yearMap = monthly[year] || {};
  const counts = { Done: 0, Pending: 0, "Not Done": 0, "Not Applicable": 0 };
  Object.values(yearMap).forEach((v) => { if (counts[v] !== undefined) counts[v]++; });
  return (
    <div className="mt-3 pt-3 border-t border-[#E5E2DC] text-[11px] text-[#5C6661] flex items-center flex-wrap gap-3">
      <span className="font-semibold text-[#121C17]">{year}:</span>
      <span className="text-emerald-700">✓ Done {counts.Done}</span>
      <span className="text-amber-700">⏳ Pending {counts.Pending}</span>
      {counts["Not Done"] > 0 && <span className="text-red-700">✗ Not done {counts["Not Done"]}</span>}
    </div>
  );
}


// Load Razorpay checkout.js once
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.getElementById("razorpay-checkout-js");
    if (existing) { existing.addEventListener("load", () => resolve(true)); return; }
    const s = document.createElement("script");
    s.id = "razorpay-checkout-js";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/**
 * After Razorpay returns success, build a polished WhatsApp message to the admin and
 * pop the wa.me link. Returns the URL it tried to open.
 */
function shareSuccessWithAdmin({ adminNumber, who, amount, paymentId, customer }) {
  const phone = (adminNumber || "7008144614").replace(/\D/g, "");
  const wa = phone.length === 10 ? `91${phone}` : phone;
  const ref = customer?.ref || who?.ref || "—";
  const service = customer?.service_history?.[0]?.service_name || who?.service_name || "—";
  const name = customer?.full_name || who?.full_name || "—";
  const mobile = customer?.mobile || who?.mobile || "—";
  const email = customer?.email || who?.email || "";
  const lines = [
    "🎉 *Payment received on FinSahayak Portal*",
    "",
    `*Customer:* ${name}`,
    `*Mobile:* +91 ${mobile}`,
  ];
  if (email) lines.push(`*Email:* ${email}`);
  lines.push(
    `*Service:* ${service}`,
    `*Reference:* ${ref}`,
    "",
    `*Amount paid:* ₹${Number(amount).toLocaleString("en-IN")}`,
    `*Razorpay payment ID:* ${paymentId}`,
    `*Paid at:* ${new Date().toLocaleString("en-IN")}`,
    "",
    "Kindly start the work and update the portal. Thank you!",
    "— Sent automatically from finsahayak.com",
  );
  const url = `https://wa.me/${wa}?text=${encodeURIComponent(lines.join("\n"))}`;
  window.open(url, "_blank", "noopener,noreferrer");
  return url;
}

function PostPaySuccessCard({ amount, paymentId, customerRef, onClose, onShareAdmin }) {
  return (
    <div className="mt-4 bento-card p-5 sm:p-6 border-2 border-emerald-300 bg-emerald-50/40" data-testid="portal-pay-success">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center"><Check size={18} /></div>
        <div className="flex-1">
          <div className="text-sm font-display font-medium text-[#121C17]">Payment received — thank you!</div>
          <div className="text-xs text-[#5C6661] mt-0.5">
            ₹{Number(amount).toLocaleString("en-IN")} paid · ID <span className="font-mono">{paymentId}</span>
            {customerRef ? <> · You're now customer <span className="font-mono">{customerRef}</span></> : null}
          </div>
        </div>
        <button onClick={onClose} className="rounded-full p-1.5 text-[#5C6661] hover:bg-white/60" aria-label="Dismiss">✕</button>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={onShareAdmin} data-testid="portal-pay-share-admin"
          className="rounded-full px-4 py-2 text-sm font-medium text-white inline-flex items-center gap-1.5" style={{ background: "#25D366" }}>
          <MessageCircle size={14} /> Notify admin on WhatsApp
        </button>
        <a href={`${API}/receipts/${paymentId.startsWith('pay_') ? '' : paymentId}`} target="_blank" rel="noopener noreferrer"
          className="rounded-full px-4 py-2 text-sm font-medium border border-[#E5E2DC] inline-flex items-center gap-1.5 text-[#1E4D3B] hover:bg-white"
          style={{ display: 'none' }}>
          <FileDown size={14} /> Receipt
        </a>
      </div>
    </div>
  );
}

function PayBento({ data, ref, onPaid, adminWa = "7008144614" }) {
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(null); // {amount, paymentId}

  const openRazorpay = async () => {
    setPaying(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) { toast.error("Could not load Razorpay. Check your internet."); setPaying(false); return; }
      const { data: order } = await api.post("/razorpay/create-order", {});
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: order.name,
        description: order.description,
        prefill: order.prefill,
        theme: { color: "#1E4D3B" },
        handler: async (response) => {
          try {
            const { data: verify } = await api.post("/razorpay/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            const amt = (order.amount || 0) / 100;
            setSuccess({ amount: amt, paymentId: response.razorpay_payment_id, verify });
            toast.success("Payment received. Thank you!");
            // Auto-pop WA-to-admin
            shareSuccessWithAdmin({
              adminNumber: adminWa, amount: amt,
              paymentId: response.razorpay_payment_id, customer: verify?.customer || data,
            });
            onPaid && onPaid();
          } catch (e) {
            toast.error(getErr(e, "Payment verification failed"));
          } finally {
            setPaying(false);
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on && rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed");
        setPaying(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(getErr(e, "Could not start payment"));
      setPaying(false);
    }
  };

  if (success) {
    return (
      <PostPaySuccessCard
        amount={success.amount}
        paymentId={success.paymentId}
        customerRef={success.verify?.customer_ref || null}
        onClose={() => setSuccess(null)}
        onShareAdmin={() => shareSuccessWithAdmin({
          adminNumber: adminWa, amount: success.amount,
          paymentId: success.paymentId, customer: success.verify?.customer || data,
        })}
      />
    );
  }

  return (
    <div className="mt-4 bento-card p-5 sm:p-6 border-2 border-amber-300 bg-amber-50/40 flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="portal-pay-now">
      <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 grid place-items-center"><Wallet size={18} /></div>
      <div className="flex-1">
        <div className="text-sm font-display font-medium text-[#121C17]">Pay your outstanding balance</div>
        <div className="text-xs text-[#5C6661] mt-0.5">₹{data.total_due.toLocaleString("en-IN")} pending. Pay securely via Razorpay (UPI, cards, netbanking, wallets).</div>
      </div>
      <div className="flex flex-wrap gap-2 sm:justify-end">
        <button
          onClick={openRazorpay}
          disabled={paying}
          data-testid="portal-pay-razorpay"
          className="rounded-full px-5 py-2.5 text-sm font-medium text-white inline-flex items-center gap-1.5 disabled:opacity-60"
          style={{ background: "#1E4D3B" }}>
          <CreditCard size={14} /> {paying ? "Opening…" : `Pay ₹${data.total_due.toLocaleString("en-IN")}`}
        </button>
        {data.payment_link && (
          <a href={data.payment_link} target="_blank" rel="noopener noreferrer" data-testid="portal-pay-link"
            className="rounded-full px-4 py-2.5 text-sm font-medium text-white inline-flex items-center gap-1.5" style={{ background: "#D17B5D" }}>
            <ExternalLink size={14} /> Hosted link
          </a>
        )}
        <a href={waLink(`Hi, I want to pay my outstanding ₹${data.total_due.toLocaleString("en-IN")}. Ref: ${ref}.`)} target="_blank" rel="noopener noreferrer" data-testid="portal-pay-whatsapp"
          className="rounded-full px-4 py-2.5 text-sm font-medium text-white inline-flex items-center gap-1.5" style={{ background: "#25D366" }}>
          <MessageCircle size={14} /> WhatsApp
        </a>
      </div>
    </div>
  );
}

function LeadPayBento({ data, ref, onPaid, adminWa = "7008144614" }) {
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(null);

  const openRazorpay = async () => {
    setPaying(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) { toast.error("Could not load Razorpay. Check your internet."); setPaying(false); return; }
      const { data: order } = await api.post("/razorpay/create-order", {});
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: order.name,
        description: order.description,
        prefill: order.prefill,
        theme: { color: "#1E4D3B" },
        handler: async (response) => {
          try {
            const { data: verify } = await api.post("/razorpay/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            const amt = (order.amount || 0) / 100;
            setSuccess({ amount: amt, paymentId: response.razorpay_payment_id, verify });
            toast.success("Payment received — you are now a FinSahayak customer 🎉");
            shareSuccessWithAdmin({
              adminNumber: adminWa, amount: amt,
              paymentId: response.razorpay_payment_id,
              customer: verify?.customer || data,
              who: data,
            });
            // Re-load /portal/me so the page refreshes into the customer view
            onPaid && onPaid();
          } catch (e) {
            toast.error(getErr(e, "Payment verification failed"));
          } finally {
            setPaying(false);
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on && rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed");
        setPaying(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(getErr(e, "Could not start payment"));
      setPaying(false);
    }
  };

  if (success) {
    return (
      <PostPaySuccessCard
        amount={success.amount}
        paymentId={success.paymentId}
        customerRef={success.verify?.customer_ref || null}
        onClose={() => setSuccess(null)}
        onShareAdmin={() => shareSuccessWithAdmin({
          adminNumber: adminWa, amount: success.amount,
          paymentId: success.paymentId,
          customer: success.verify?.customer || data,
          who: data,
        })}
      />
    );
  }

  return (
    <div className="mt-4 bento-card p-5 sm:p-6 border-2 border-[#1E4D3B]/30 bg-[#1E4D3B]/5 flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="portal-lead-pay-now">
      <div className="w-10 h-10 rounded-xl bg-[#1E4D3B]/10 text-[#1E4D3B] grid place-items-center"><CreditCard size={18} /></div>
      <div className="flex-1">
        <div className="text-sm font-display font-medium text-[#121C17]">Pay now to start your service faster</div>
        <div className="text-xs text-[#5C6661] mt-0.5">
          Service: <strong>{data.service_name}</strong> · ₹{Number(data.service_price || 0).toLocaleString("en-IN")}. Once paid, our team gets notified instantly and starts on your work.
        </div>
      </div>
      <div className="flex flex-wrap gap-2 sm:justify-end">
        <button
          onClick={openRazorpay}
          disabled={paying || !(data.service_price > 0)}
          data-testid="portal-lead-pay-razorpay"
          className="rounded-full px-5 py-2.5 text-sm font-medium text-white inline-flex items-center gap-1.5 disabled:opacity-60"
          style={{ background: "#1E4D3B" }}>
          <CreditCard size={14} /> {paying ? "Opening…" : `Pay ₹${Number(data.service_price || 0).toLocaleString("en-IN")}`}
        </button>
        <a href={waLink(`Hi FinSahayak, I have just submitted an application (Ref: ${ref}) for ${data.service_name}. Please assist me with the payment.`)} target="_blank" rel="noopener noreferrer" data-testid="portal-lead-pay-whatsapp"
          className="rounded-full px-4 py-2.5 text-sm font-medium text-white inline-flex items-center gap-1.5" style={{ background: "#25D366" }}>
          <MessageCircle size={14} /> Help on WhatsApp
        </a>
      </div>
    </div>
  );
}
